import {yupResolver} from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import {useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {Form, Popup, Button, Icon} from 'semantic-ui-react';
import * as yup from 'yup';

import {getChangedFieldsValues} from 'forms';
import httpClient from 'httpClient';
import {getShortURLHosts} from 'state/selectors';

import UserSearch from '../Common/AuthSearch/UserSearch';

import styles from './ShortUrlForm.module.scss';

function buildShortURLsHostsDropdownOptions(shortURLsHosts) {
  return shortURLsHosts.map(opt => ({
    key: opt,
    text: opt,
    value: opt,
  }));
}

const formSchema = yup.object().shape({
  targetUrl: yup
    .string()
    .url('Target URL must be a valid URL')
    .required('Target URL is required'),
  host: yup.string().when('$isEdit', (isEdit, schema) => {
    if (isEdit) {
      return;
    }

    return schema.required("Short URL's host is required");
  }),
  slug: yup.string().when('$isEdit', (isEdit, schema) => {
    if (isEdit) {
      return;
    }

    return schema
      .required('Slug is required')
      .matches(
        /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/,
        "Slug must consist of lowercase alphanumeric characters or '-', and must start and end with an alphanumeric character"
      )
      .min(3, 'Minimum allowed length for a slug is 3 characters');
  }),
});

const defaultFormValues = {
  targetUrl: '',
  slug: '',
  owner: undefined,
  description: '',
  appendQuery: false,
};

const ShortUrlForm = ({initialValues, onSubmit, allowOwnerChange, loading}) => {
  const shortURLsHosts = useSelector(getShortURLHosts);
  const isEdit = initialValues !== null && initialValues !== undefined;

  const {
    control,
    getValues,
    setValue,
    reset,
    formState: {isValid, isDirty, isSubmitting, dirtyFields},
  } = useForm({
    mode: 'onChange',
    defaultValues: {...defaultFormValues, host: shortURLsHosts[0]},
    resolver: yupResolver(formSchema),
    context: {isEdit},
  });

  useEffect(() => {
    if (!loading && initialValues) {
      const {url, targetUrl, slug, owner, description, appendQuery} = initialValues;
      const shortUrlHost = new URL(url).host;

      reset({
        targetUrl,
        host: shortUrlHost,
        slug,
        owner,
        description,
        appendQuery,
      });
    }
  }, [initialValues, loading, reset, setValue]);

  return (
    <Form
      onSubmit={() => {
        !isEdit
          ? onSubmit(getValues())
          : onSubmit(getChangedFieldsValues(dirtyFields, getValues()));
      }}
      loading={isSubmitting || loading}
    >
      <Controller
        control={control}
        name="targetUrl"
        render={({field: {onChange, value, name}, fieldState: {error}}) => (
          <Popup
            trigger={
              <Form.Input
                id="targetUrl"
                label="Target URL"
                error={error?.message}
                required
                name={name}
                value={value}
                placeholder="https://my-site-at-cern.web.cern.ch"
                onChange={(_evt, data) => {
                  onChange(data.value);
                }}
              />
            }
            position="left center"
          >
            <Popup.Content>Specify the destination URL of the redirection.</Popup.Content>
          </Popup>
        )}
      />

      <label htmlFor="slug" className={styles.SlugLabel}>
        Slug
      </label>

      <Form.Group>
        <Controller
          control={control}
          name="host"
          render={({field: {onChange, value, name}, fieldState: {error}}) => (
            <Form.Dropdown
              name={name}
              value={value}
              options={buildShortURLsHostsDropdownOptions(shortURLsHosts)}
              error={error?.message}
              selection
              required
              width={5}
              disabled={isEdit}
              onChange={(_evt, data) => onChange(data.value)}
            />
          )}
        />

        <Controller
          control={control}
          name="slug"
          render={({field: {onChange, value, name}, fieldState: {error}}) => (
            <Popup
              trigger={
                <Form.Input
                  className={styles.SlugInput}
                  id="slug"
                  error={error?.message}
                  required
                  name={name}
                  value={value}
                  placeholder="xyz123"
                  width={11}
                  disabled={isEdit}
                  icon={
                    !isEdit && (
                      <Icon
                        name="refresh"
                        link
                        onClick={async () => {
                          const {data: slug} = await httpClient.get(
                            `/api/web-redirector/${getValues().host}/generate-slug`
                          );

                          setValue('slug', slug, {shouldValidate: true});
                        }}
                      />
                    )
                  }
                  onChange={(_evt, data) => {
                    onChange(data.value);
                  }}
                />
              }
              position="right center"
              wide
            >
              <Popup.Content>
                The desired slug for your short URL. You can generate one by clicking on the refresh
                icon. The slug is case insensitive.
              </Popup.Content>
            </Popup>
          )}
        />
      </Form.Group>

      {allowOwnerChange && (
        <Controller
          control={control}
          name="owner"
          render={({field: {onChange, value, name}, fieldState: {error}}) => (
            <Popup
              trigger={
                <UserSearch
                  id="owner"
                  label="Owner"
                  name={name}
                  value={value}
                  error={error?.message}
                  onChange={data => {
                    onChange(data.upn);
                  }}
                  includeSecondaryAccounts
                  includeServiceAccounts
                />
              }
              position="left center"
            >
              <Popup.Content>
                The owner of the short URL. Only a primary account can be set as the owner of a
                short URL when changing its ownership.
              </Popup.Content>
            </Popup>
          )}
        />
      )}

      <Controller
        control={control}
        name="description"
        render={({field: {onChange, value, name}, fieldState: {error}}) => (
          <Popup
            trigger={
              <Form.TextArea
                id="description"
                label="Description"
                name={name}
                value={value}
                error={error?.message}
                placeholder="Description"
                onChange={(_evt, data) => {
                  onChange(data.value);
                }}
              />
            }
            position="left center"
          >
            <Popup.Content>Description of the short URL's purpose.</Popup.Content>
          </Popup>
        )}
      />

      <Controller
        control={control}
        name="appendQuery"
        render={({field: {onChange, value, name}}) => (
          <Popup
            trigger={
              <Form.Checkbox
                id="appendQuery"
                name={name}
                checked={value}
                label={
                  <label htmlFor="appendQuery">
                    Append URL path and query parameters to redirection
                  </label>
                }
                disabled={isEdit}
                onChange={(_evt, data) => onChange(data.checked)}
              />
            }
            position="left center"
          >
            <Popup.Content>
              {`Extra URL paths and query parameters will be appended to the final URL, e.g. cern.ch/<Slug>/my-path/?items=1234 will redirect to <Target URL>/my-path/?items=1234. This should only be used to preserve the behavior of existing short URLs.`}
            </Popup.Content>
          </Popup>
        )}
      />
      <Button
        type="submit"
        className={styles.ActionButton}
        floated="right"
        disabled={!isValid || !isDirty || isSubmitting || loading}
      >
        {isEdit ? 'Update' : 'Create'}
      </Button>
    </Form>
  );
};

ShortUrlForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  allowOwnerChange: PropTypes.bool,
  initialValues: PropTypes.object,
  loading: PropTypes.bool,
};

export default ShortUrlForm;
